import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AcsButton, AcsCard, AcsInput, AcsLogo, AcsPageShell, AcsStatusBadge } from '../components';
import { useI18n } from '../i18n/I18nProvider';
import { api, type PolicyDetail, type PolicySummary } from '../api/client';
import { PolicyPreviewDialog, countPlaceholders } from './PolicyPreviewDialog';
import { MarkdownEditor } from '../components/MarkdownEditor';
import { nextVersion } from './version.js';

/**
 * Informative privacy: creazione, modifica della bozza, pubblicazione.
 *
 * Il ciclo di vita è rigido di proposito (§6.7): una bozza si modifica quanto
 * serve, una versione pubblicata non si tocca più. La schermata lo rende
 * evidente — sulle versioni pubblicate non esiste il pulsante «Modifica», e il
 * motivo è scritto accanto.
 */
export function PoliciesPage() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ version: '', language: 'it', title: '', content: '' });
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<PolicyDetail | null>(null);
  const [duplicatedFrom, setDuplicatedFrom] = useState<string | null>(null);
  /** Istante dell'ultimo salvataggio riuscito: conferma che il lavoro è al sicuro. */
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const policies = useQuery({ queryKey: ['policies'], queryFn: api.listPolicies });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['policies'] });
    setCreating(false);
    setEditingId(null);
    setPreviewing(null);
    setDuplicatedFrom(null);
    setSavedAt(null);
    setError(null);
  };

  /**
   * Pubblicazione con conferma.
   *
   * Se restano segnaposto lo dice nel messaggio: è l'ultimo momento utile,
   * perché dopo la pubblicazione correggere significa creare una nuova versione.
   */
  function confirmPublish(id: string, content: string): void {
    const remaining = countPlaceholders(content);
    const message =
      remaining > 0
        ? `${t.policies.publishWithPlaceholders.replace('{n}', String(remaining))}\n\n${t.policies.publishConfirm}`
        : t.policies.publishConfirm;
    if (window.confirm(message)) publish.mutate(id);
  }

  /**
   * Duplica un'informativa in una nuova bozza.
   *
   * Non tocca l'originale: precompila il modulo di creazione con il testo
   * esistente e una versione incrementata, che l'utente conferma o cambia. È il
   * percorso previsto per correggere un documento già pubblicato, dato che
   * quello non è modificabile (§6.7).
   */
  function duplicate(id: string): void {
    setError(null);
    void api
      .getPolicy(id)
      .then((full) => {
        setCreating(true);
        setEditingId(null);
        setPreviewing(null);
        setDuplicatedFrom(full.version);
        setForm({
          version: nextVersion(full.version),
          language: full.language,
          title: full.title,
          content: full.content,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((cause: Error) => setError(cause.message));
  }

  function openPreview(id: string): void {
    setError(null);
    void api
      .getPolicy(id)
      .then(setPreviewing)
      .catch((cause: Error) => setError(cause.message));
  }

  /**
   * Salvataggio riuscito: l'editor **resta aperto**.
   *
   * Chiuderlo e riportare all'elenco costringerebbe a ritrovare il documento e
   * riaprirlo per la modifica successiva — dopo aver appena scritto un testo
   * lungo è il momento peggiore per farlo. Si prosegue nello stesso posto, con
   * la conferma del salvataggio e la pubblicazione a portata di mano.
   */
  function keepEditing(saved: PolicyDetail): void {
    setCreating(false);
    setEditingId(saved.id);
    setDuplicatedFrom(null);
    setError(null);
    setSavedAt(Date.now());
    void queryClient.invalidateQueries({ queryKey: ['policies'] });
  }

  const create = useMutation({
    mutationFn: () =>
      api.createPolicy({
        version: form.version.trim(),
        language: form.language,
        title: form.title.trim(),
        content: form.content,
      }),
    onSuccess: keepEditing,
    onError: (cause: Error) => setError(cause.message),
  });

  const update = useMutation({
    mutationFn: (id: string) =>
      api.updatePolicyDraft(id, { title: form.title.trim(), content: form.content }),
    onSuccess: keepEditing,
    onError: (cause: Error) => setError(cause.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deletePolicyDraft(id),
    onSuccess: refresh,
    onError: (cause: Error) => setError(cause.message),
  });

  const retire = useMutation({
    mutationFn: (id: string) => api.retirePolicy(id),
    onSuccess: refresh,
    onError: (cause: Error) => setError(cause.message),
  });

  const publish = useMutation({
    mutationFn: (id: string) => api.publishPolicy(id),
    onSuccess: refresh,
    onError: (cause: Error) => setError(cause.message),
  });

  function startEdit(policy: PolicySummary & { content?: string }): void {
    setEditingId(policy.id);
    setCreating(false);
    setForm({
      version: policy.version,
      language: policy.language,
      title: policy.title,
      content: policy.content ?? '',
    });
  }

  const list = policies.data?.policies ?? [];

  return (
    <AcsPageShell header={<AcsLogo />}>
      <Link to="/app" style={{ color: 'var(--acs-color-primary)' }}>
        ← {t.app.back}
      </Link>
      <h1>{t.policies.title}</h1>

      {/* Il ciclo bozza → pubblicata è la regola centrale del versionamento:
          spiegarlo qui evita che l'irreversibilità sia una sorpresa. */}
      <p style={{ color: 'var(--acs-color-text-muted)', maxWidth: '48rem' }}>
        {t.policies.lifecycleExplain}
      </p>

      {error ? (
        <p className="acs-field__error" role="alert">
          {error}
        </p>
      ) : null}

      {!creating && editingId === null ? (
        <AcsButton
          onClick={() => {
            setCreating(true);
            setForm({ version: '', language: 'it', title: '', content: '' });
          }}
        >
          {t.policies.newNotice}
        </AcsButton>
      ) : null}

      {creating || editingId !== null ? (
        <AcsCard>
          <h2 style={{ marginTop: 0 }}>
            {creating ? t.policies.newNotice : t.policies.edit}
          </h2>

          {duplicatedFrom ? (
            <p role="status" style={{ color: 'var(--acs-color-text-muted)' }}>
              {t.policies.duplicatedFrom.replace('{version}', duplicatedFrom)}
            </p>
          ) : null}

          {/* Conferma che il lavoro è al sicuro, senza spostare l'utente altrove. */}
          {savedAt ? (
            <p
              role="status"
              style={{
                background: 'var(--acs-tone-success-bg)',
                color: 'var(--acs-tone-success-fg)',
                padding: 'var(--acs-space-3)',
                borderRadius: 'var(--acs-radius-md)',
              }}
            >
              {t.policies.savedKeepEditing}
            </p>
          ) : null}

          <AcsInput
            label={t.policies.version}
            value={form.version}
            onChange={(e) => setForm({ ...form, version: e.target.value })}
            autoComplete="off"
            // Versione e lingua identificano il documento: su una bozza esistente
            // non si cambiano, si crea semmai un'altra versione.
            disabled={!creating}
            required
          />
          <label className="acs-field">
            <span className="acs-field__label">{t.policies.language}</span>
            <select
              className="acs-input"
              value={form.language}
              onChange={(e) => setForm({ ...form, language: e.target.value })}
              disabled={!creating}
            >
              <option value="it">IT</option>
              <option value="en">EN</option>
            </select>
          </label>
          <AcsInput
            label={t.policies.noticeTitle}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            autoComplete="off"
            required
          />
          <div className="acs-field">
            <label className="acs-field__label" htmlFor="policy-content">
              {t.policies.content}
            </label>
            <MarkdownEditor
              id="policy-content"
              value={form.content}
              onChange={(content) => setForm({ ...form, content })}
            />
          </div>

          <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap' }}>
            <AcsButton
              disabled={create.isPending || update.isPending}
              onClick={() => {
                setError(null);
                if (creating) create.mutate();
                else if (editingId) update.mutate(editingId);
              }}
            >
              {creating ? t.policies.create : t.policies.save}
            </AcsButton>

            {/*
              Anteprima e pubblicazione senza uscire dall'editor: sono i due
              gesti che seguono naturalmente la scrittura, e mandare l'utente a
              cercare il documento nell'elenco per compierli è un giro inutile.
            */}
            {editingId ? (
              <>
                <AcsButton variant="secondary" onClick={() => openPreview(editingId)}>
                  {t.policies.preview}
                </AcsButton>
                <AcsButton
                  variant="secondary"
                  disabled={update.isPending || publish.isPending}
                  onClick={() => {
                    // Si pubblica ciò che è stato salvato: se ci sono modifiche
                    // non salvate si salvano prima, altrimenti si pubblicherebbe
                    // una versione diversa da quella a schermo.
                    setError(null);
                    update.mutate(editingId, {
                      onSuccess: (saved) => confirmPublish(saved.id, saved.content),
                    });
                  }}
                >
                  {t.policies.saveAndPublish}
                </AcsButton>
              </>
            ) : null}

            <AcsButton
              variant="secondary"
              onClick={() => {
                setCreating(false);
                setEditingId(null);
                setSavedAt(null);
                setError(null);
              }}
            >
              {savedAt ? t.policies.close : t.policies.cancel}
            </AcsButton>
          </div>
        </AcsCard>
      ) : null}

      <h2>{t.policies.notices}</h2>
      {list.length === 0 ? <p>{t.policies.none}</p> : null}

      <div className="acs-stack">
        {list.map((policy) => (
          <AcsCard key={policy.id}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 'var(--acs-space-4)',
                flexWrap: 'wrap',
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>
                  {policy.version} · {policy.language.toUpperCase()}
                </h3>
                <p style={{ margin: 0 }}>{policy.title}</p>
                {policy.publishedAt ? (
                  <p style={{ margin: 0, color: 'var(--acs-color-text-muted)' }}>
                    {new Date(policy.publishedAt).toLocaleString()}
                  </p>
                ) : null}
              </div>
              {/* Il tono rinforza, l'etichetta informa: il badge resta leggibile
                  anche senza percepire il colore (§20.6). */}
              <AcsStatusBadge
                tone={policy.retiredAt ? 'neutral' : policy.published ? 'success' : 'warning'}
              >
                {policy.retiredAt
                  ? t.policies.retired
                  : policy.published
                    ? t.policies.published
                    : t.policies.draft}
              </AcsStatusBadge>
            </div>

            {policy.published ? (
              <>
                <p style={{ color: 'var(--acs-color-text-muted)' }}>
                  {policy.retiredAt ? t.policies.retiredExplain : t.policies.publishedImmutable}
                </p>
                <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap' }}>
                  <AcsButton variant="secondary" onClick={() => openPreview(policy.id)}>
                    {t.policies.preview}
                  </AcsButton>
                  <AcsButton variant="secondary" onClick={() => duplicate(policy.id)}>
                    {t.policies.duplicate}
                  </AcsButton>
                  {!policy.retiredAt ? (
                    <AcsButton
                      variant="secondary"
                      onClick={() => {
                        if (window.confirm(t.policies.retireConfirm)) retire.mutate(policy.id);
                      }}
                    >
                      {t.policies.retire}
                    </AcsButton>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <p style={{ color: 'var(--acs-color-text-muted)' }}>
                  {t.policies.publishWarning}
                </p>
                <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap' }}>
                  <AcsButton variant="secondary" onClick={() => openPreview(policy.id)}>
                    {t.policies.preview}
                  </AcsButton>
                  <AcsButton variant="secondary" onClick={() => duplicate(policy.id)}>
                    {t.policies.duplicate}
                  </AcsButton>
                  <AcsButton
                    variant="secondary"
                    onClick={() => {
                      void api
                        .getPolicy(policy.id)
                        .then(startEdit)
                        .catch((cause: Error) => setError(cause.message));
                    }}
                  >
                    {t.policies.edit}
                  </AcsButton>
                  <AcsButton
                    variant="secondary"
                    onClick={() => {
                      if (window.confirm(t.policies.deleteConfirm)) remove.mutate(policy.id);
                    }}
                  >
                    {t.policies.delete}
                  </AcsButton>
                  <AcsButton
                    onClick={() => {
                      void api
                        .getPolicy(policy.id)
                        .then((full) => confirmPublish(policy.id, full.content))
                        .catch((cause: Error) => setError(cause.message));
                    }}
                  >
                    {t.policies.publish}
                  </AcsButton>
                </div>
              </>
            )}
          </AcsCard>
        ))}
      </div>

      {previewing ? (
        <PolicyPreviewDialog
          policy={previewing}
          onClose={() => setPreviewing(null)}
          {...(previewing.published
            ? {}
            : {
                onPublish: () => confirmPublish(previewing.id, previewing.content),
              })}
        />
      ) : null}
    </AcsPageShell>
  );
}
